import React, { useMemo } from 'react';
import { Check, X } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shared/ui/accordion';
import { Badge } from '@/shared/ui/badge';
import { Checkbox } from '@/shared/ui/checkbox';
import { cn } from '@/shared/utils/utils';
import type { RolePermission } from '../types';
import { groupPermissionsByFeature } from '../utils/groupPermissions';

type RolePermissionsPanelEditProps = {
  mode: 'edit';
  selectedPermissionIds: number[];
  onTogglePermission: (permissionId: number) => void;
};

type RolePermissionsPanelViewProps = {
  mode: 'view';
  includedPermissionIds: number[];
};

export type RolePermissionsPanelProps = {
  permissions: RolePermission[];
  className?: string;
  scrollClassName?: string;
  emptyMessage?: string;
} & (RolePermissionsPanelEditProps | RolePermissionsPanelViewProps);

export const RolePermissionsPanel: React.FC<RolePermissionsPanelProps> = (props) => {
  const {
    permissions,
    mode,
    className,
    scrollClassName = 'always-scrollbar max-h-[52vh] overflow-y-scroll pr-1',
    emptyMessage = 'No project permissions available.',
  } = props;

  const permissionGroups = useMemo(
    () => groupPermissionsByFeature(permissions),
    [permissions],
  );

  const allGroupKeys = useMemo(
    () => permissionGroups.map((group) => group.key),
    [permissionGroups],
  );

  const includedSet = useMemo(() => {
    if (mode !== 'view') return null;
    return new Set(props.includedPermissionIds);
  }, [mode, props]);

  const selectedSet = useMemo(() => {
    if (mode !== 'edit') return null;
    return new Set(props.selectedPermissionIds);
  }, [mode, props]);

  if (permissions.length === 0) {
    return (
      <div className={cn('rounded-md border p-3', className)}>
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={cn(scrollClassName, className)}>
      <Accordion type="multiple" defaultValue={allGroupKeys} className="space-y-2">
        {permissionGroups.map((group) => {
          const includedInGroup =
            mode === 'view'
              ? group.permissions.filter((p) => includedSet!.has(p.id)).length
              : group.permissions.filter((p) => selectedSet!.has(p.id)).length;

          return (
            <AccordionItem key={group.key} value={group.key}>
              <AccordionTrigger className="text-sm font-semibold">
                <span className="flex items-center gap-2">
                  {group.name}
                  <Badge variant="secondary" className="font-normal">
                    {mode === 'view'
                      ? `${includedInGroup} / ${group.permissions.length}`
                      : group.permissions.length}
                  </Badge>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 pb-3">
                  {group.permissions.map((permission) => {
                    const isIncluded =
                      mode === 'edit'
                        ? selectedSet!.has(permission.id)
                        : includedSet!.has(permission.id);

                    const rowClassName = cn(
                      'flex items-center justify-between gap-3 rounded-md border p-2',
                      mode === 'view' && !isIncluded && 'border-dashed bg-muted/30',
                      mode === 'edit' && 'cursor-pointer',
                    );

                    const content = (
                      <>
                        <div>
                          <p
                            className={cn(
                              'text-sm font-medium',
                              isIncluded ? 'text-foreground' : 'text-muted-foreground',
                            )}
                          >
                            {permission.label}
                          </p>
                          {permission.description ? (
                            <p className="text-xs text-muted-foreground">{permission.description}</p>
                          ) : (
                            <p className="text-xs text-muted-foreground">{permission.codename}</p>
                          )}
                        </div>
                        {mode === 'edit' ? (
                          <Checkbox
                            checked={isIncluded}
                            onCheckedChange={() => props.onTogglePermission(permission.id)}
                          />
                        ) : isIncluded ? (
                          <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-primary">
                            <Check className="h-3.5 w-3.5" aria-hidden />
                            Included
                          </span>
                        ) : (
                          <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                            <X className="h-3.5 w-3.5" aria-hidden />
                            Not included
                          </span>
                        )}
                      </>
                    );

                    if (mode === 'edit') {
                      return (
                        <label key={permission.id} className={rowClassName}>
                          {content}
                        </label>
                      );
                    }

                    return (
                      <div key={permission.id} className={rowClassName}>
                        {content}
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};
